import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Divider,
  Slider,
} from '@mui/material';
import {
  LocalTaxi as TaxiIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  QrCode as QrCodeIcon,
  ContentCopy as CopyIcon,
  AttachMoney as MoneyIcon,
  Hotel as HotelIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as CalendarIcon,
  Print as PrintIcon,
  PhoneAndroid as PhoneIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { FirebaseContext } from 'common';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import { QRCodeSVG } from 'qrcode.react';

export default function PartnerPortal() {
  const { partnerCode } = useParams();
  const navigate = useNavigate();
  const firebase = useContext(FirebaseContext);

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);

  // Portal state
  const [activeTab, setActiveTab] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Booking form
  const [bookingForm, setBookingForm] = useState({
    customerName: '',
    customerPhone: '',
    passengers: 1,
    notes: '',
  });
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);

  // Pricing state (for subadmin)
  const [pricingForm, setPricingForm] = useState({
    percentageMarkup: 0,
    fixedFee: 0,
    discountPercent: 0,
  });
  const [pricingLoading, setPricingLoading] = useState(false);

  // QR Code state
  const [qrDialog, setQrDialog] = useState(false);
  const [qrSize, setQrSize] = useState(256);

  // Load partner data
  useEffect(() => {
    if (!firebase?.database || !partnerCode) {
      setLoading(false);
      return;
    }

    firebase.database.ref('turpris_partners')
      .orderByChild('partnerCode')
      .equalTo(partnerCode)
      .once('value')
      .then(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const partnerId = Object.keys(data)[0];
          const partnerData = { ...data[partnerId], id: partnerId };
          if (partnerData.active) {
            setPartner(partnerData);
            setPricingForm({
              percentageMarkup: partnerData.percentageMarkup || 0,
              fixedFee: partnerData.fixedFee || 0,
              discountPercent: partnerData.discountPercent || 0,
            });
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load partner:', err);
        setLoading(false);
      });

    // Check for saved session
    const savedUser = localStorage.getItem(`partner_user_${partnerCode}`);
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
        setIsLoggedIn(true);
      } catch (e) {
        localStorage.removeItem(`partner_user_${partnerCode}`);
      }
    }
  }, [firebase?.database, partnerCode]);

  // Load partner bookings
  useEffect(() => {
    if (!firebase?.database || !partner?.id || !isLoggedIn) return;

    const bookingsRef = firebase.database.ref('guest_bookings')
      .orderByChild('partner/id')
      .equalTo(partner.id);

    bookingsRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setBookings(list);
      } else {
        setBookings([]);
      }
    });

    return () => bookingsRef.off();
  }, [firebase?.database, partner?.id, isLoggedIn]);

  // Handle login
  const handleLogin = async () => {
    if (!loginForm.username || !loginForm.password) {
      setLoginError('Vennligst fyll inn brukernavn og passord');
      return;
    }

    setLoginLoading(true);
    setLoginError('');

    try {
      const snapshot = await firebase.database.ref('turpris_partner_users')
        .orderByChild('username')
        .equalTo(loginForm.username)
        .once('value');

      if (!snapshot.exists()) {
        setLoginError('Ugyldig brukernavn eller passord');
        setLoginLoading(false);
        return;
      }

      const data = snapshot.val();
      const userId = Object.keys(data)[0];
      const user = data[userId];

      // Check password
      if (user.password !== loginForm.password) {
        setLoginError('Ugyldig brukernavn eller passord');
        setLoginLoading(false);
        return;
      }

      // Check if user belongs to this partner
      if (user.partnerCode !== partnerCode) {
        setLoginError('Denne brukeren har ikke tilgang til denne portalen');
        setLoginLoading(false);
        return;
      }

      // Check if active
      if (!user.active) {
        setLoginError('Denne brukeren er deaktivert');
        setLoginLoading(false);
        return;
      }

      // Save session
      const userSession = { ...user, id: userId };
      localStorage.setItem(`partner_user_${partnerCode}`, JSON.stringify(userSession));
      setCurrentUser(userSession);
      setIsLoggedIn(true);

      // Log login
      await firebase.database.ref(`turpris_partner_users/${userId}`).update({
        lastLoginAt: new Date().toISOString(),
      });

    } catch (err) {
      setLoginError('Innlogging feilet: ' + err.message);
    }
    setLoginLoading(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem(`partner_user_${partnerCode}`);
    setCurrentUser(null);
    setIsLoggedIn(false);
    setLoginForm({ username: '', password: '' });
  };

  // Calculate price
  const calculatePrice = async () => {
    if (!origin?.coords || !destination?.coords) return;

    setPriceLoading(true);
    try {
      const service = new window.google.maps.DistanceMatrixService();
      const result = await new Promise((resolve, reject) => {
        service.getDistanceMatrix(
          {
            origins: [new window.google.maps.LatLng(origin.coords.lat, origin.coords.lng)],
            destinations: [new window.google.maps.LatLng(destination.coords.lat, destination.coords.lng)],
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === 'OK') resolve(response);
            else reject(new Error('Distance Matrix failed'));
          }
        );
      });

      const element = result.rows[0]?.elements[0];
      if (element?.status !== 'OK') throw new Error('No route found');

      const distanceKm = element.distance.value / 1000;
      const durationMin = element.duration.value / 60;

      // Call backend fare API
      const projectId = firebase?.config?.projectId || 'plukk-no';
      const host = `https://us-central1-${projectId}.cloudfunctions.net`;

      const fareResponse = await fetch(`${host}/calculate_fare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          distance_km: distanceKm,
          duration_min: durationMin,
          pax: bookingForm.passengers,
          datetime_iso: new Date().toISOString(),
          wheelchair: false,
        }),
      });

      let baseTotal = 0;
      if (fareResponse.ok) {
        const fareData = await fareResponse.json();
        baseTotal = fareData?.breakdown?.total || 0;
      }

      // Apply partner pricing
      let finalPrice = baseTotal;
      if (partner.percentageMarkup > 0) {
        finalPrice += baseTotal * (partner.percentageMarkup / 100);
      }
      if (partner.fixedFee > 0) {
        finalPrice += partner.fixedFee;
      }
      if (partner.discountPercent > 0) {
        finalPrice -= finalPrice * (partner.discountPercent / 100);
      }

      setEstimatedPrice({
        total: Math.round(finalPrice),
        baseTotal: Math.round(baseTotal),
        distance: distanceKm,
        duration: durationMin,
        distanceText: element.distance.text,
        durationText: element.duration.text,
      });

    } catch (err) {
      console.error('Price calculation failed:', err);
      setSnackbar({ open: true, message: 'Kunne ikke beregne pris', severity: 'error' });
    }
    setPriceLoading(false);
  };

  // Create booking
  const handleCreateBooking = async () => {
    if (!origin?.coords || !destination?.coords || !bookingForm.customerName) {
      setSnackbar({ open: true, message: 'Vennligst fyll inn alle påkrevde felt', severity: 'error' });
      return;
    }

    setBookingLoading(true);
    try {
      const bookingId = generateBookingId();
      const bookingData = {
        id: bookingId,
        pickup: {
          address: origin.description,
          coords: origin.coords,
        },
        dropoff: {
          address: destination.description,
          coords: destination.coords,
        },
        customer: {
          name: bookingForm.customerName,
          phone: bookingForm.customerPhone,
          isGuest: true,
        },
        passengers: bookingForm.passengers,
        notes: bookingForm.notes,
        status: 'pending',
        createdAt: new Date().toISOString(),
        source: 'partner_portal',
        partner: {
          id: partner.id,
          code: partnerCode,
          name: partner.name,
          type: partner.type,
          commissionPercent: partner.commissionPercent || 0,
        },
        bookedBy: {
          userId: currentUser.id,
          username: currentUser.username,
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
        },
        estimate: estimatedPrice ? {
          total: estimatedPrice.total,
          baseTotal: estimatedPrice.baseTotal,
          currency: 'NOK',
        } : null,
        route: estimatedPrice ? {
          distanceKm: estimatedPrice.distance,
          durationMin: estimatedPrice.duration,
          distanceText: estimatedPrice.distanceText,
          durationText: estimatedPrice.durationText,
        } : null,
      };

      await firebase.database.ref(`guest_bookings/${bookingId}`).set(bookingData);

      setBookingSuccess(bookingData);
      setSnackbar({ open: true, message: 'Booking opprettet!', severity: 'success' });

      // Reset form
      setBookingForm({ customerName: '', customerPhone: '', passengers: 1, notes: '' });
      setOrigin(null);
      setDestination(null);
      setEstimatedPrice(null);

    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setBookingLoading(false);
  };

  // Generate booking ID
  const generateBookingId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  // Update pricing (subadmin only)
  const handleUpdatePricing = async () => {
    if (currentUser?.role !== 'subadmin') return;

    setPricingLoading(true);
    try {
      await firebase.database.ref(`turpris_partners/${partner.id}`).update({
        percentageMarkup: pricingForm.percentageMarkup,
        fixedFee: pricingForm.fixedFee,
        discountPercent: pricingForm.discountPercent,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.id,
      });
      setPartner({ ...partner, ...pricingForm });
      setSnackbar({ open: true, message: 'Prisinnstillinger lagret!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Feil: ' + err.message, severity: 'error' });
    }
    setPricingLoading(false);
  };

  // Calculate commission
  const calculateCommission = () => {
    if (!partner?.commissionPercent) return 0;
    const completedBookings = bookings.filter(b => b.status === 'completed');
    const totalRevenue = completedBookings.reduce((sum, b) => sum + (b.estimate?.total || 0), 0);
    return Math.round(totalRevenue * (partner.commissionPercent / 100));
  };

  // Get booking URL for QR code
  const getBookingUrl = () => {
    return `${window.location.origin}/turpris/landing?partner=${partnerCode}`;
  };

  // Print QR code
  const handlePrintQR = () => {
    const printContent = document.getElementById('qr-print-area');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Bestill Taxi - ${partner.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: white;
            }
            .container {
              text-align: center;
              padding: 40px;
              border: 3px solid #6366f1;
              border-radius: 20px;
              max-width: 400px;
            }
            .logo {
              font-size: 32px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .logo span {
              background: linear-gradient(135deg, #6366f1, #8b5cf6);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            .partner-name {
              font-size: 24px;
              color: #6366f1;
              margin-bottom: 20px;
            }
            .qr-container {
              background: white;
              padding: 20px;
              border-radius: 10px;
              display: inline-block;
              margin: 20px 0;
            }
            .instructions {
              font-size: 18px;
              color: #374151;
              margin-top: 20px;
            }
            .step {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin: 10px 0;
              font-size: 16px;
            }
            .step-number {
              background: #6366f1;
              color: white;
              width: 28px;
              height: 28px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
            }
            .discount {
              background: #dcfce7;
              color: #166534;
              padding: 8px 16px;
              border-radius: 20px;
              font-weight: bold;
              margin-top: 15px;
              display: inline-block;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="logo">Tur<span>Pris</span></div>
            <div class="partner-name">${partner.name}</div>
            <div class="qr-container">
              ${document.getElementById('qr-svg-container').innerHTML}
            </div>
            <div class="instructions">
              <div class="step"><span class="step-number">1</span> Skann QR-koden</div>
              <div class="step"><span class="step-number">2</span> Velg destinasjon</div>
              <div class="step"><span class="step-number">3</span> Bestill taxi</div>
            </div>
            ${partner.discountPercent > 0 ? `<div class="discount">${partner.discountPercent}% rabatt!</div>` : ''}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Download QR code as image
  const handleDownloadQR = () => {
    const svg = document.getElementById('qr-svg-container')?.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = qrSize * 2; // Higher resolution
    canvas.width = size;
    canvas.height = size;

    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);

      const link = document.createElement('a');
      link.download = `taxi-qr-${partner.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = url;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!partner) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <Typography variant="h5">Partner ikke funnet</Typography>
        <Typography color="text.secondary">Koden "{partnerCode}" er ugyldig eller deaktivert</Typography>
        <Button variant="contained" onClick={() => navigate('/turpris/landing')}>
          Gå til hovedsiden
        </Button>
      </Box>
    );
  }

  // Login screen
  if (!isLoggedIn) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Box textAlign="center" mb={4}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <HotelIcon sx={{ color: 'white', fontSize: 30 }} />
              </Box>
              <Typography variant="h5" fontWeight="bold">{partner.name}</Typography>
              <Typography color="text.secondary">Partner Portal</Typography>
            </Box>

            {loginError && (
              <Alert severity="error" sx={{ mb: 3 }}>{loginError}</Alert>
            )}

            <TextField
              fullWidth
              label="Brukernavn"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Passord"
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              sx={{ mb: 3 }}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loginLoading}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
            >
              {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Logg inn'}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Main portal
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 0 }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HotelIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">{partner.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentUser?.firstName} {currentUser?.lastName} ({currentUser?.role === 'subadmin' ? 'Sub-admin' : 'Bruker'})
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              color="inherit"
            >
              Logg ut
            </Button>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Stats for subadmin */}
        {currentUser?.role === 'subadmin' && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ReceiptIcon color="primary" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">{bookings.length}</Typography>
                      <Typography variant="caption" color="text.secondary">Totalt bookinger</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon color="warning" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {bookings.filter(b => b.status === 'pending').length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Venter</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingIcon color="success" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {bookings.filter(b => b.status === 'completed').length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Fullført</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <MoneyIcon color="success" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">{calculateCommission()} kr</Typography>
                      <Typography variant="caption" color="text.secondary">Provisjon</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<TaxiIcon />} label="Ny booking" />
            <Tab icon={<QrCodeIcon />} label="QR Bestilling" />
            <Tab icon={<ReceiptIcon />} label="Bookinger" />
            {currentUser?.role === 'subadmin' && (
              <Tab icon={<SettingsIcon />} label="Innstillinger" />
            )}
          </Tabs>

          {/* New Booking Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Bestill taxi for gjest
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <GoogleMapsAutoComplete
                      placeholder="Hentested"
                      value={origin?.description || ''}
                      onChange={(value) => {
                        setOrigin(value);
                        setEstimatedPrice(null);
                      }}
                    />
                    <GoogleMapsAutoComplete
                      placeholder="Destinasjon"
                      value={destination?.description || ''}
                      onChange={(value) => {
                        setDestination(value);
                        setEstimatedPrice(null);
                      }}
                    />

                    <Divider />

                    <TextField
                      fullWidth
                      label="Gjestnavn *"
                      value={bookingForm.customerName}
                      onChange={(e) => setBookingForm({ ...bookingForm, customerName: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={bookingForm.customerPhone}
                      onChange={(e) => setBookingForm({ ...bookingForm, customerPhone: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      label="Antall passasjerer"
                      type="number"
                      value={bookingForm.passengers}
                      onChange={(e) => setBookingForm({ ...bookingForm, passengers: parseInt(e.target.value) || 1 })}
                      InputProps={{ inputProps: { min: 1, max: 8 } }}
                    />
                    <TextField
                      fullWidth
                      label="Merknad"
                      multiline
                      rows={2}
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', bgcolor: '#f9fafb' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Prisestimat
                      </Typography>

                      {!origin?.coords || !destination?.coords ? (
                        <Alert severity="info">Velg hentested og destinasjon for å se pris</Alert>
                      ) : (
                        <>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={calculatePrice}
                            disabled={priceLoading}
                            sx={{ mb: 2 }}
                          >
                            {priceLoading ? <CircularProgress size={20} /> : 'Beregn pris'}
                          </Button>

                          {estimatedPrice && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h3" fontWeight="bold" color="primary">
                                {estimatedPrice.total} kr
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {estimatedPrice.distanceText} • {estimatedPrice.durationText}
                              </Typography>
                              {estimatedPrice.baseTotal !== estimatedPrice.total && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Grunnpris: {estimatedPrice.baseTotal} kr
                                </Typography>
                              )}
                            </Box>
                          )}
                        </>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleCreateBooking}
                        disabled={bookingLoading || !origin?.coords || !destination?.coords || !bookingForm.customerName}
                        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                      >
                        {bookingLoading ? <CircularProgress size={24} color="inherit" /> : 'Bestill taxi'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* QR Code Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }} id="qr-print-area">
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Bestill Taxi QR-kode
                    </Typography>
                    <Typography color="text.secondary" gutterBottom>
                      Gjester kan skanne denne koden for å bestille taxi direkte
                    </Typography>

                    <Card sx={{ mt: 3, p: 4, display: 'inline-block' }}>
                      <Box
                        id="qr-svg-container"
                        sx={{
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 2,
                          display: 'inline-block',
                        }}
                      >
                        <QRCodeSVG
                          value={getBookingUrl()}
                          size={qrSize}
                          level="H"
                          includeMargin={true}
                          bgColor="white"
                          fgColor="#1f2937"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {partner.name}
                      </Typography>
                      {partner.discountPercent > 0 && (
                        <Chip
                          label={`${partner.discountPercent}% rabatt`}
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Card>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handlePrintQR}
                        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                      >
                        Skriv ut plakat
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadQR}
                      >
                        Last ned QR
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CopyIcon />}
                        onClick={() => {
                          navigator.clipboard.writeText(getBookingUrl());
                          setSnackbar({ open: true, message: 'URL kopiert!', severity: 'success' });
                        }}
                      >
                        Kopier URL
                      </Button>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        QR Størrelse
                      </Typography>
                      <Slider
                        value={qrSize}
                        onChange={(e, val) => setQrSize(val)}
                        min={128}
                        max={512}
                        step={32}
                        sx={{ width: 200, mx: 'auto', display: 'block', color: '#6366f1' }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ bgcolor: '#f9fafb', height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Slik fungerer det
                      </Typography>

                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: '#6366f1',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}
                          >
                            1
                          </Box>
                          <Box>
                            <Typography fontWeight="bold">Skann QR-koden</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Gjesten åpner kamera-appen og skanner koden
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: '#6366f1',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}
                          >
                            2
                          </Box>
                          <Box>
                            <Typography fontWeight="bold">Velg destinasjon</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Gjesten skriver inn hvor de skal og ser prisen
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: '#6366f1',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}
                          >
                            3
                          </Box>
                          <Box>
                            <Typography fontWeight="bold">Bestill taxi</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Gjesten fyller inn kontaktinfo og bestiller
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      <Alert severity="info" icon={<PhoneIcon />}>
                        <Typography variant="body2">
                          <strong>Tips:</strong> Skriv ut plakaten og plasser den i resepsjonen,
                          på rommet, eller andre synlige steder for gjestene.
                        </Typography>
                      </Alert>

                      <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                          Partner fordeler
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Alle bestillinger via denne QR-koden registreres automatisk
                          på din partner-konto med {partner.commissionPercent || 0}% provisjon.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Bookings Tab */}
          {activeTab === 2 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking ID</TableCell>
                    <TableCell>Gjest</TableCell>
                    <TableCell>Rute</TableCell>
                    <TableCell>Pris</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" py={4}>
                          Ingen bookinger ennå
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>
                          <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                            {booking.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{booking.customer?.name || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.customer?.phone || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                            {booking.pickup?.address?.substring(0, 30)}...
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                            → {booking.dropoff?.address?.substring(0, 30)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {booking.estimate ? (
                            <Typography fontWeight="bold" color="primary">
                              {booking.estimate.total} kr
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={booking.status}
                            color={
                              booking.status === 'completed' ? 'success' :
                              booking.status === 'accepted' ? 'info' :
                              'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(booking.createdAt).toLocaleString('nb-NO', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Settings Tab (subadmin only) */}
          {activeTab === 3 && currentUser?.role === 'subadmin' && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Prisinnstillinger
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                Juster prisene for bookinger via din partner-portal
              </Typography>

              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Prosentpåslag (+{pricingForm.percentageMarkup}%)
                      </Typography>
                      <Slider
                        value={pricingForm.percentageMarkup}
                        onChange={(e, val) => setPricingForm({ ...pricingForm, percentageMarkup: val })}
                        min={0}
                        max={50}
                        step={1}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 25, label: '25%' },
                          { value: 50, label: '50%' },
                        ]}
                        valueLabelDisplay="auto"
                        sx={{ color: '#6366f1' }}
                      />

                      <TextField
                        fullWidth
                        label="Fast tillegg"
                        type="number"
                        value={pricingForm.fixedFee}
                        onChange={(e) => setPricingForm({ ...pricingForm, fixedFee: parseInt(e.target.value) || 0 })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">+</InputAdornment>,
                          endAdornment: <InputAdornment position="end">kr</InputAdornment>,
                        }}
                        sx={{ mt: 3, mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="Rabatt til gjester"
                        type="number"
                        value={pricingForm.discountPercent}
                        onChange={(e) => setPricingForm({ ...pricingForm, discountPercent: parseInt(e.target.value) || 0 })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">-</InputAdornment>,
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        sx={{ mb: 3 }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleUpdatePricing}
                        disabled={pricingLoading}
                        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                      >
                        {pricingLoading ? <CircularProgress size={20} /> : 'Lagre endringer'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ bgcolor: '#f0fdf4' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Provisjonsoversikt
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography color="text.secondary">Provisjonssats:</Typography>
                        <Typography fontWeight="bold">{partner.commissionPercent || 0}%</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography color="text.secondary">Fullførte turer:</Typography>
                        <Typography fontWeight="bold">
                          {bookings.filter(b => b.status === 'completed').length}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography color="text.secondary">Total omsetning:</Typography>
                        <Typography fontWeight="bold">
                          {bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.estimate?.total || 0), 0)} kr
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold">Opptjent provisjon:</Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {calculateCommission()} kr
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Booking Success Dialog */}
      <Dialog open={!!bookingSuccess} onClose={() => setBookingSuccess(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Booking opprettet!</DialogTitle>
        <DialogContent>
          {bookingSuccess && (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <TaxiIcon sx={{ fontSize: 40, color: '#22c55e' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                {bookingSuccess.id}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                Booking ID
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography>{bookingSuccess.customer?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingSuccess.pickup?.address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                → {bookingSuccess.dropoff?.address}
              </Typography>
              {bookingSuccess.estimate && (
                <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mt: 2 }}>
                  {bookingSuccess.estimate.total} kr
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingSuccess(null)} variant="contained">
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Alert
        severity={snackbar.severity}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          display: snackbar.open ? 'flex' : 'none',
          zIndex: 9999,
        }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        {snackbar.message}
      </Alert>
    </Box>
  );
}
